import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2, Check } from 'lucide-react';
import { City } from '@/types/city';
import { searchCities } from '@/lib/services/geocoding';
import { extractCityName } from '@/lib/utils/text';
import { cn } from '@/lib/utils';
import { useDebounce } from 'use-debounce';
import { ComboboxInput } from './ComboboxInput';

interface CitySearchComboboxProps {
  value: string;
  onChange: (city: string) => void;
  placeholder?: string;
  className?: string;
  error?: string;
  country?: string;
}

export const CitySearchCombobox: React.FC<CitySearchComboboxProps> = ({
  value,
  onChange,
  placeholder = "Enter city...",
  className,
  error,
  country
}) => {
  const [searchTerm, setSearchTerm] = useState(value);
  const [debounced] = useDebounce(searchTerm, 250);
  const [suggestions, setSuggestions] = useState<City[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  useEffect(() => {
    const fetch = async () => {
      if (!debounced || debounced.length < 2) {
        setSuggestions([]);
        return;
      }
      setIsLoading(true);
      try {
        const results = await searchCities(debounced);
        const mapped = results
          .filter(c => c.name)
          .map(c => ({ ...c, name: extractCityName(c.name) }));

        const unique = mapped.filter(
          (c, i, arr) =>
            arr.findIndex(x => x.name.toLowerCase() === c.name.toLowerCase()) === i
        );

        const filtered = country
          ? unique.filter(c => c.country?.toLowerCase() === country.toLowerCase())
          : unique;

        setSuggestions(filtered);
        setSearchError(filtered.length === 0 ? 'No cities found' : null);
      } catch {
        setSearchError('Failed to search');
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
  }, [debounced, country]);

  const handleSelect = (city: City) => {
    const name = extractCityName(city.name);
    setSelectedCity(city);
    setSearchTerm(name);
    onChange(name);
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchTerm(val);
    setSelectedCity(null);
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (selectedCity) {
        onChange(extractCityName(selectedCity.name));
      } else if (searchTerm.length > 2) {
        onChange(searchTerm);
      }
      setIsOpen(false);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (selectedCity) {
        setSearchTerm(extractCityName(selectedCity.name));
        onChange(extractCityName(selectedCity.name));
      } else if (searchTerm.length > 2) {
        onChange(searchTerm);
      }
      setIsOpen(false);
    }, 100);
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <ComboboxInput
        ref={inputRef}
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
        leftIcon={<MapPin className="h-5 w-5 text-gold-400" />}
        rightIcon={
          isLoading ? (
            <Loader2 className="h-4 w-4 text-gold-400 animate-spin" />
          ) : selectedCity ? (
            <Check className="h-4 w-4 text-green-500" />
          ) : null
        }
        error={!!(error || searchError)}
        className="h-9"
      />

      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-dark-200 border border-dark-400 rounded-xl shadow-lg top-full">
          <div className="py-1 max-h-[200px] overflow-y-auto">
            {suggestions.map((city, idx) => (
              <button
                key={`${city.name}-${idx}`}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(city)}
                className={cn(
                  "w-full flex items-center px-4 py-2 text-left hover:bg-dark-300",
                  searchTerm === city.name && "bg-dark-300"
                )}
              >
                <MapPin className="h-4 w-4 text-dark-800 mr-2" />
                <div>
                  <div className="text-white font-medium">{city.name}</div>
                  {(city.state || city.country) && (
                    <div className="text-dark-800 text-xs">
                      {[city.state, city.country].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {searchError && (
        <p className="text-red-500 text-sm mt-1">{searchError}</p>
      )}
    </div>
  );
};